import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Truck, Container, Plus, Trash2 } from "lucide-react";

const TYPES = ["Hotshot", "Gooseneck", "Flatbed", "Step Deck", "Dry Van", "Reefer", "Power Only", "Box Truck", "Sprinter Van", "Cargo Van", "Car Hauler", "Other"];

export default function Equipment() {
  const [p, setP] = useState(null);
  const [truck, setTruck] = useState({ make: "", model: "", year: "", plate: "" });
  const [trailer, setTrailer] = useState({ equipment_type: "Gooseneck", length: "", payload: "" });

  const fetch = () => api.get("/carrier/profile").then((r) => setP(r.data));
  useEffect(() => { fetch(); }, []);
  if (!p) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-blue" size={32} /></div></DashboardShell>;

  const addTruck = async () => { await api.post("/carrier/equipment", { kind: "truck", ...truck }); toast.success("Truck added"); setTruck({ make: "", model: "", year: "", plate: "" }); fetch(); };
  const addTrailer = async () => { await api.post("/carrier/equipment", { kind: "trailer", ...trailer, length: parseFloat(trailer.length) || 0, payload: parseFloat(trailer.payload) || 0 }); toast.success("Trailer added"); setTrailer({ equipment_type: "Gooseneck", length: "", payload: "" }); fetch(); };
  const del = async (kind, id) => { await api.delete(`/carrier/equipment/${kind}/${id}`); toast.success("Removed"); fetch(); };

  return (
    <DashboardShell nav={carrierNav} title="Equipment">
      <p className="text-xd-muted -mt-4 mb-6">Add your trucks and trailers so XD can match you to the right freight.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Trucks */}
        <div className="bg-xd-paper border border-xd-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Truck size={18} className="text-xd-blue" /> Trucks</h2>
          <div className="mt-4 space-y-2">
            {(p.trucks || []).map((t, i) => (
              <div key={t.id || i} className="flex items-center justify-between bg-xd-surface border border-xd-border rounded-lg px-4 py-2.5">
                <span className="text-sm">{t.year} {t.make} {t.model} {t.plate && `· ${t.plate}`}</span>
                {t.id && <button data-testid={`del-truck-${i}`} onClick={() => del("truck", t.id)} className="text-xd-dim hover:text-xd-red"><Trash2 size={15} /></button>}
              </div>
            ))}
            {(!p.trucks || !p.trucks.length) && <p className="text-sm text-xd-dim">No trucks yet.</p>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Input data-testid="truck-make" placeholder="Make" value={truck.make} onChange={(e) => setTruck({ ...truck, make: e.target.value })} className="bg-xd-surface border-xd-border" />
            <Input data-testid="truck-model" placeholder="Model" value={truck.model} onChange={(e) => setTruck({ ...truck, model: e.target.value })} className="bg-xd-surface border-xd-border" />
            <Input data-testid="truck-year" placeholder="Year" value={truck.year} onChange={(e) => setTruck({ ...truck, year: e.target.value })} className="bg-xd-surface border-xd-border" />
            <Input data-testid="truck-plate" placeholder="Plate" value={truck.plate} onChange={(e) => setTruck({ ...truck, plate: e.target.value })} className="bg-xd-surface border-xd-border" />
          </div>
          <Button data-testid="add-truck-btn" onClick={addTruck} className="mt-3 w-full bg-xd-blue hover:bg-xd-blueH text-white rounded-xl"><Plus size={16} className="mr-2" /> Add Truck</Button>
        </div>

        {/* Trailers */}
        <div className="bg-xd-paper border border-xd-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Container size={18} className="text-xd-gold" /> Trailers</h2>
          <div className="mt-4 space-y-2">
            {(p.trailers || []).map((t, i) => (
              <div key={t.id || i} className="flex items-center justify-between bg-xd-surface border border-xd-border rounded-lg px-4 py-2.5">
                <span className="text-sm">{t.equipment_type || t.type} {t.length ? `· ${t.length}ft` : ""} {t.payload ? `· ${t.payload} lbs` : ""}</span>
                {t.id && <button data-testid={`del-trailer-${i}`} onClick={() => del("trailer", t.id)} className="text-xd-dim hover:text-xd-red"><Trash2 size={15} /></button>}
              </div>
            ))}
            {(!p.trailers || !p.trailers.length) && <p className="text-sm text-xd-dim">No trailers yet.</p>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Select value={trailer.equipment_type} onValueChange={(v) => setTrailer({ ...trailer, equipment_type: v })}>
              <SelectTrigger data-testid="trailer-type" className="col-span-2 bg-xd-surface border-xd-border"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="trailer-length" placeholder="Length (ft)" value={trailer.length} onChange={(e) => setTrailer({ ...trailer, length: e.target.value })} className="bg-xd-surface border-xd-border" />
            <Input data-testid="trailer-payload" placeholder="Payload (lbs)" value={trailer.payload} onChange={(e) => setTrailer({ ...trailer, payload: e.target.value })} className="bg-xd-surface border-xd-border" />
          </div>
          <Button data-testid="add-trailer-btn" onClick={addTrailer} className="mt-3 w-full bg-xd-gold hover:bg-xd-goldH text-xd-bg font-semibold rounded-xl"><Plus size={16} className="mr-2" /> Add Trailer</Button>
        </div>
      </div>
    </DashboardShell>
  );
}
